import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Gavel, Loader2, Plus, Trash2, ExternalLink, ChevronLeft, MapPin, Euro, Sparkles,
  ArrowRightCircle, CheckCircle2, Pencil, Wallet,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n));
const ESTADOS_LICITACION = ["En preparación", "Presentada", "En evaluación", "Adjudicada a Valoriza", "Adjudicada a terceros", "Desierta / Anulada"];
const ESTADO_COLORS = {
  "En preparación": "bg-slate-100 text-slate-700 border-slate-200",
  "Presentada": "bg-blue-100 text-blue-700 border-blue-200",
  "En evaluación": "bg-amber-100 text-amber-700 border-amber-200",
  "Adjudicada a Valoriza": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Adjudicada a terceros": "bg-red-50 text-red-500 border-red-200",
  "Desierta / Anulada": "bg-red-50 text-red-500 border-red-200",
};

export default function TendersPage() {
  const { token } = useAuth();
  const authH = { Authorization: `Bearer ${token}` };
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/tenders`, { headers: authH });
      if (r.ok) setTenders(await r.json());
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (selected) {
    return <TenderDetail tenderId={selected} authH={authH} onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="space-y-6" data-testid="tenders-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Gavel className="w-6 h-6 text-indigo-600" />Licitaciones I+D</h1>
          <p className="text-sm text-muted-foreground mt-1">Aportaciones e iniciativas digitales propuestas por Innovación en contratos/licitaciones. Convierte una iniciativa en Proyecto PG-07 o Piloto, o encuentra coincidencias con lo que ya tenéis.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="tender-create-btn"><Plus className="w-4 h-4 mr-1" />Nueva licitación</Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="tenders-grid">
          {tenders.map((t, i) => (
            <Card key={t.id} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setSelected(t.id)} data-testid={`tender-card-${i}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <Badge className={ESTADO_COLORS[t.estado] || ""}>{t.estado}</Badge>
                  <Badge variant="outline" className="text-[10px]">{t.n_iniciativas} iniciativa(s)</Badge>
                </div>
                <div className="font-bold text-sm leading-tight">{t.nombre_contrato}</div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{t.ciudad}</span>
                  <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{eur(t.presupuesto_base_licitacion)}</span>
                </div>
                <div className="text-[11px] text-muted-foreground">{t.entidad_contratante} {t.expediente ? `· Exp. ${t.expediente}` : ""}</div>
                {t.fecha_limite_presentacion_ofertas && <div className="text-[11px] text-muted-foreground">Plazo ofertas: {t.fecha_limite_presentacion_ofertas}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TenderCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} authH={authH} onCreated={() => { setCreateOpen(false); load(); }} />
    </div>
  );
}

function TenderDetail({ tenderId, authH, onBack }) {
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createInitOpen, setCreateInitOpen] = useState(false);
  const [matchResults, setMatchResults] = useState({});
  const [matching, setMatching] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/tenders/${tenderId}`, { headers: authH });
      if (r.ok) setTender(await r.json());
    } finally { setLoading(false); }
  }, [tenderId, authH]);

  useEffect(() => { load(); }, [load]);

  const changeEstado = async (estado) => {
    const r = await fetch(`${API}/tenders/${tenderId}`, { method: "PUT", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({ estado }) });
    if (r.ok) { toast.success("Estado actualizado"); load(); }
  };

  const runAiMatch = async (initId) => {
    setMatching(m => ({ ...m, [initId]: true }));
    try {
      const r = await fetch(`${API}/tenders/initiatives/${initId}/ai-match`, { method: "POST", headers: authH });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      const data = await r.json();
      setMatchResults(m => ({ ...m, [initId]: data.matches }));
      if (!data.matches.length) toast.info("Sin coincidencias relevantes en el catálogo existente");
    } catch (e) { toast.error(`Error IA: ${e.message}`); }
    finally { setMatching(m => ({ ...m, [initId]: false })); }
  };

  const convert = async (initId, targetType) => {
    if (!window.confirm(`¿Convertir esta iniciativa en ${targetType === "pilot" ? "Piloto" : "Proyecto PG-07"}?`)) return;
    const r = await fetch(`${API}/tenders/initiatives/${initId}/convert`, {
      method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify({ target_type: targetType }),
    });
    if (r.ok) { toast.success(`Convertida a ${targetType === "pilot" ? "Piloto" : "Proyecto PG-07"}`); load(); }
    else toast.error((await r.json()).detail || "Error al convertir");
  };

  const deleteInitiative = async (initId) => {
    if (!window.confirm("¿Eliminar esta iniciativa?")) return;
    const r = await fetch(`${API}/tenders/initiatives/${initId}`, { method: "DELETE", headers: authH });
    if (r.ok) { toast.success("Iniciativa eliminada"); load(); }
  };

  if (loading || !tender) return <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>;

  const grouped = {};
  (tender.initiatives || []).forEach(i => { (grouped[i.dominio || "Sin dominio"] = grouped[i.dominio || "Sin dominio"] || []).push(i); });

  return (
    <div className="space-y-6" data-testid="tender-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="tender-detail-back"><ChevronLeft className="w-4 h-4 mr-1" />Volver</Button>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <h2 className="text-lg font-bold">{tender.nombre_contrato}</h2>
            <Select value={tender.estado} onValueChange={changeEstado}>
              <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="tender-estado-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS_LICITACION.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <div><span className="text-muted-foreground">Entidad:</span> <strong>{tender.entidad_contratante}</strong></div>
            <div><span className="text-muted-foreground">Ciudad:</span> <strong>{tender.ciudad}</strong></div>
            <div><span className="text-muted-foreground">Expediente:</span> <strong>{tender.expediente || "—"}</strong></div>
            <div><span className="text-muted-foreground">Presupuesto:</span> <strong>{eur(tender.presupuesto_base_licitacion)}</strong></div>
            <div><span className="text-muted-foreground">Plazo ejecución:</span> <strong>{tender.plazo_ejecucion || "—"}</strong></div>
            <div><span className="text-muted-foreground">Plazo ofertas:</span> <strong>{tender.fecha_limite_presentacion_ofertas || "—"}</strong></div>
          </div>
          {tender.plataforma_central && <div className="text-xs"><span className="text-muted-foreground">Plataforma propuesta:</span> <strong>{tender.plataforma_central}</strong></div>}
          {tender.notas && <p className="text-xs text-muted-foreground bg-muted/40 rounded p-2">{tender.notas}</p>}
          {tender.fuente_datos && <p className="text-[10px] text-muted-foreground italic">Fuente: {tender.fuente_datos}</p>}
          {tender.url_licitacion && (
            <a href={tender.url_licitacion} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1 hover:underline" data-testid="tender-url-link">
              <ExternalLink className="w-3 h-3" />Ver licitación
            </a>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="font-bold">Iniciativas digitales ({tender.initiatives?.length || 0})</h3>
        <Button size="sm" onClick={() => setCreateInitOpen(true)} data-testid="initiative-create-btn"><Plus className="w-4 h-4 mr-1" />Nueva iniciativa</Button>
      </div>

      {Object.entries(grouped).map(([dominio, items]) => (
        <div key={dominio} className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-wide text-indigo-600">{dominio}</div>
          {items.map((init, i) => (
            <Card key={init.id} data-testid={`initiative-card-${i}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm">{init.nombre}</div>
                    <p className="text-xs text-muted-foreground mt-0.5">{init.descripcion}</p>
                    <div className="flex gap-3 text-[10px] text-muted-foreground mt-1 flex-wrap">
                      {init.tecnologia && <span>🔧 {init.tecnologia}</span>}
                      {init.kpi_objetivo && <span>🎯 {init.kpi_objetivo}</span>}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 text-red-500" onClick={() => deleteInitiative(init.id)} data-testid={`initiative-delete-${init.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>

                {init.estado_conversion === "Convertida" ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`initiative-converted-${init.id}`}>
                    <CheckCircle2 className="w-3 h-3 mr-1" />Convertida a {init.converted_to_type === "pilot" ? "Piloto" : "Proyecto PG-07"}
                  </Badge>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => convert(init.id, "pilot")} data-testid={`initiative-convert-pilot-${init.id}`}>
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />Convertir a Piloto
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => convert(init.id, "pg07")} data-testid={`initiative-convert-pg07-${init.id}`}>
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" />Convertir a Proyecto PG-07
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-600" onClick={() => runAiMatch(init.id)} disabled={matching[init.id]} data-testid={`initiative-ai-match-${init.id}`}>
                      {matching[init.id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}Buscar coincidencias IA
                    </Button>
                  </div>
                )}

                {matchResults[init.id] && matchResults[init.id].length > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 space-y-1" data-testid={`initiative-match-results-${init.id}`}>
                    {matchResults[init.id].map((m, mi) => (
                      <div key={mi} className="text-xs">
                        <strong>{m.name}</strong> <Badge variant="outline" className="text-[9px] ml-1">{m.score}% match</Badge>
                        <div className="text-[11px] text-muted-foreground">{m.reason}</div>
                      </div>
                    ))}
                  </div>
                )}

                {init.desglose_economico && <DesgloseEconomico d={init.desglose_economico} testId={`initiative-desglose-${init.id}`} />}
              </CardContent>
            </Card>
          ))}
        </div>
      ))}

      <InitiativeCreateDialog open={createInitOpen} onClose={() => setCreateInitOpen(false)} authH={authH} tenderId={tenderId} onCreated={() => { setCreateInitOpen(false); load(); }} />
    </div>
  );
}

// Desglose económico de una iniciativa (memoria de gestión informatizada, 2026-08-24):
// coste anual por partida + CAPEX/OPEX + % sobre el tipo de licitación.
function DesgloseEconomico({ d, testId }) {
  const rows = Object.entries(d).filter(([k, v]) => v && typeof v === "object" && "total" in v);
  return (
    <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-3 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800"><Wallet className="w-3.5 h-3.5" />Desglose económico (memoria técnica)</div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-muted-foreground border-b">
              <th className="text-left py-1 pr-2">Partida</th>
              <th className="text-right px-1">Año 1</th>
              <th className="text-right px-1">Año 2</th>
              <th className="text-right px-1">Año 3</th>
              <th className="text-right px-1">Año 4</th>
              <th className="text-right px-1">Año 5</th>
              <th className="text-right pl-2 font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, v]) => (
              <tr key={label} className={label.startsWith("total") ? "font-bold border-t" : ""}>
                <td className="py-1 pr-2">{label}</td>
                <td className="text-right px-1">{eur(v.anio_1)}</td>
                <td className="text-right px-1">{eur(v.anio_2)}</td>
                <td className="text-right px-1">{eur(v.anio_3)}</td>
                <td className="text-right px-1">{eur(v.anio_4)}</td>
                <td className="text-right px-1">{eur(v.anio_5)}</td>
                <td className="text-right pl-2 font-bold">{eur(v.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] pt-1 border-t">
        <div><span className="text-muted-foreground">CAPEX inicial:</span> <strong>{eur(d.capex_inicial)}</strong> <span className="text-muted-foreground">({eur(d.capex_equipamiento)} equip. + {eur(d.capex_horas_internas_importe)}/{d.capex_horas_internas_horas}h internas)</span></div>
        <div><span className="text-muted-foreground">Amortización:</span> <strong>{d.capex_amortizacion_meses} meses</strong> → {eur(d.capex_anual)}/año</div>
        <div><span className="text-muted-foreground">Total 5 años (con IVA):</span> <strong>{eur(d.total_con_iva_5_anios)}</strong></div>
        <div><span className="text-muted-foreground">% sobre tipo licitación:</span> <strong>{d.pct_sobre_tipo_licitacion}%</strong> <span className="text-muted-foreground">(plataforma sola: {d.pct_plataforma_sola_sobre_tipo_licitacion}%)</span></div>
      </div>
      {(d.reparto_capex_pct != null || d.reparto_opex_pct != null) && (
        <div className="text-[11px] text-muted-foreground">Reparto: <strong>{d.reparto_capex_pct}% CAPEX</strong> / <strong>{d.reparto_opex_pct}% OPEX</strong> — en el OPEX manda el personal, no la tecnología.</div>
      )}
      {d.nota && <p className="text-[10px] text-muted-foreground italic">{d.nota}</p>}
    </div>
  );
}

function TenderCreateDialog({ open, onClose, authH, onCreated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ nombre_contrato: "", entidad_contratante: "", ciudad: "", expediente: "", presupuesto_base_licitacion: "", estado: "En preparación", url_licitacion: "", notas: "" });
  }, [open]);

  const submit = async () => {
    if (!form.nombre_contrato?.trim()) { toast.error("El nombre del contrato es obligatorio"); return; }
    setSaving(true);
    try {
      const body = { ...form, presupuesto_base_licitacion: form.presupuesto_base_licitacion ? parseFloat(form.presupuesto_base_licitacion) : null };
      const r = await fetch(`${API}/tenders`, { method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      toast.success("Licitación creada");
      onCreated();
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tender-create-dialog">
        <DialogHeader><DialogTitle>Nueva licitación</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <div><Label className="text-xs">Nombre del contrato *</Label><Input value={form.nombre_contrato || ""} onChange={e => setForm({ ...form, nombre_contrato: e.target.value })} data-testid="tender-create-nombre" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Entidad contratante</Label><Input value={form.entidad_contratante || ""} onChange={e => setForm({ ...form, entidad_contratante: e.target.value })} data-testid="tender-create-entidad" /></div>
            <div><Label className="text-xs">Ciudad</Label><Input value={form.ciudad || ""} onChange={e => setForm({ ...form, ciudad: e.target.value })} data-testid="tender-create-ciudad" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Expediente</Label><Input value={form.expediente || ""} onChange={e => setForm({ ...form, expediente: e.target.value })} data-testid="tender-create-expediente" /></div>
            <div><Label className="text-xs">Presupuesto (€)</Label><Input type="number" value={form.presupuesto_base_licitacion || ""} onChange={e => setForm({ ...form, presupuesto_base_licitacion: e.target.value })} data-testid="tender-create-presupuesto" /></div>
          </div>
          <div><Label className="text-xs">URL licitación</Label><Input value={form.url_licitacion || ""} onChange={e => setForm({ ...form, url_licitacion: e.target.value })} data-testid="tender-create-url" /></div>
          <div><Label className="text-xs">Notas</Label><Textarea rows={2} value={form.notas || ""} onChange={e => setForm({ ...form, notas: e.target.value })} data-testid="tender-create-notas" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="tender-create-save">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InitiativeCreateDialog({ open, onClose, authH, tenderId, onCreated }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ dominio: "", nombre: "", descripcion: "", tecnologia: "", kpi_objetivo: "" }); }, [open]);

  const submit = async () => {
    if (!form.nombre?.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/tenders/${tenderId}/initiatives`, { method: "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(form) });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      toast.success("Iniciativa añadida");
      onCreated();
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="initiative-create-dialog">
        <DialogHeader><DialogTitle>Nueva iniciativa digital</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Dominio</Label><Input value={form.dominio || ""} onChange={e => setForm({ ...form, dominio: e.target.value })} placeholder="Ej. A · Conocimiento del árbol y del suelo" data-testid="initiative-create-dominio" /></div>
          <div><Label className="text-xs">Nombre *</Label><Input value={form.nombre || ""} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="initiative-create-nombre" /></div>
          <div><Label className="text-xs">Descripción</Label><Textarea rows={2} value={form.descripcion || ""} onChange={e => setForm({ ...form, descripcion: e.target.value })} data-testid="initiative-create-descripcion" /></div>
          <div><Label className="text-xs">Tecnología</Label><Input value={form.tecnologia || ""} onChange={e => setForm({ ...form, tecnologia: e.target.value })} data-testid="initiative-create-tecnologia" /></div>
          <div><Label className="text-xs">KPI objetivo</Label><Input value={form.kpi_objetivo || ""} onChange={e => setForm({ ...form, kpi_objetivo: e.target.value })} data-testid="initiative-create-kpi" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="initiative-create-save">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}Añadir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
